import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getEffectiveGalleryLayout, getGalleryItemGridStyle, getGalleryCellAspectRatio } from '../utils/galleryLayout';

/**
 * @param {{ url: string, title?: string }[]} images
 * @param {string} [layout] — stored `${sectionId}_galleryLayout`
 * @param {number} [imgMinHeight]
 * @param {object} [gap] — MUI spacing for gap (number = theme spacing)
 * @param {string} [captionFontSize] — e.g. '0.45rem' preview / 'caption' full
 * @param {(url: string) => void} [onImageClick]
 * @param {boolean} [enlargeOnHover]
 */
function GalleryImageMosaic({
  images,
  layout,
  imgMinHeight = 80,
  gap = 0.5,
  captionFontSize = '0.45rem',
  onImageClick,
  enlargeOnHover = false,
}) {
  const list = (images || []).filter((img) => img?.url);
  const n = list.length;
  if (!n) return null;

  const effectiveLayout = getEffectiveGalleryLayout(layout, n);

  const [carouselIndex, setCarouselIndex] = useState(0);
  useEffect(() => {
    setCarouselIndex((i) => Math.min(Math.max(0, i), Math.max(0, n - 1)));
  }, [n]);

  const hoverSx =
    enlargeOnHover && onImageClick
      ? { transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }
      : onImageClick
        ? { '&:hover': { opacity: 0.92 } }
        : {};

  const titleOverlay = (img) =>
    img.title ? (
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
          p: 0.5,
          zIndex: 1,
        }}
      >
        <Typography sx={{ fontSize: captionFontSize, color: 'white', fontWeight: 600 }} noWrap>
          {img.title}
        </Typography>
      </Box>
    ) : null;

  /** @param {string | null} aspectRatio */
  const renderCell = (img, aspectRatio) => {
    if (aspectRatio) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: aspectRatio.replace(/\s/g, ''),
            borderRadius: '4px',
            overflow: 'hidden',
            cursor: onImageClick ? 'pointer' : 'default',
            ...hoverSx,
          }}
          onClick={onImageClick ? () => onImageClick(img.url) : undefined}
        >
          <Box
            component="img"
            src={img.url}
            alt={img.title || ''}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {titleOverlay(img)}
        </Box>
      );
    }
    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: '4px',
          overflow: 'hidden',
          cursor: onImageClick ? 'pointer' : 'default',
          minHeight: imgMinHeight,
          ...hoverSx,
        }}
        onClick={onImageClick ? () => onImageClick(img.url) : undefined}
      >
        <Box
          component="img"
          src={img.url}
          alt={img.title || ''}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: imgMinHeight,
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {titleOverlay(img)}
      </Box>
    );
  };

  if (effectiveLayout === 'horizontal') {
    const compact = imgMinHeight < 100;
    const carouselMaxHeight = compact ? 132 : 300;
    const btnSize = compact ? 28 : 36;
    const iconSize = compact ? 18 : 22;
    const img = list[carouselIndex];
    if (!img) return null;

    const go = (delta) => {
      setCarouselIndex((i) => Math.min(Math.max(0, i + delta), n - 1));
    };

    const navBtnSx = {
      flexShrink: 0,
      width: btnSize,
      height: btnSize,
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      color: 'text.primary',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      '&:hover': { bgcolor: 'grey.50', borderColor: 'action.disabled' },
      '&.Mui-disabled': { opacity: 0.35 },
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 0.75 : 1.25,
          width: '100%',
          py: 0.5,
        }}
      >
        {n > 1 ? (
          <IconButton
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            disabled={carouselIndex <= 0}
            size="small"
            sx={navBtnSx}
          >
            <ChevronLeftIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        ) : (
          <Box sx={{ width: btnSize, flexShrink: 0 }} aria-hidden />
        )}

        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            maxWidth: compact ? '58%' : '72%',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 18px rgba(0,0,0,0.1)',
            bgcolor: 'background.paper',
            aspectRatio: '3 / 4',
            maxHeight: carouselMaxHeight,
            mx: 'auto',
            position: 'relative',
            cursor: onImageClick ? 'pointer' : 'default',
            ...(enlargeOnHover && onImageClick
              ? { transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.01)' } }
              : {}),
          }}
          onClick={onImageClick ? () => onImageClick(img.url) : undefined}
        >
          <Box
            component="img"
            src={img.url}
            alt={img.title || ''}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {img.title && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                p: compact ? 0.5 : 1,
              }}
            >
              <Typography sx={{ fontSize: captionFontSize, color: 'white', fontWeight: 600 }} noWrap>
                {img.title}
              </Typography>
            </Box>
          )}
        </Box>

        {n > 1 ? (
          <IconButton
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            disabled={carouselIndex >= n - 1}
            size="small"
            sx={navBtnSx}
          >
            <ChevronRightIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        ) : (
          <Box sx={{ width: btnSize, flexShrink: 0 }} aria-hidden />
        )}
      </Box>
    );
  }

  if (effectiveLayout === 'vertical') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
        {list.map((img, idx) => (
          <Box key={idx}>{renderCell(img, null)}</Box>
        ))}
      </Box>
    );
  }

  const isGrid6 = effectiveLayout === 'grid6';
  return (
    <Box
      sx={{
        display: 'grid',
        gap,
        gridTemplateColumns: isGrid6 ? '1fr 1fr 1fr' : '1fr 1fr',
        gridAutoRows: 'auto',
        alignItems: 'stretch',
      }}
    >
      {list.map((img, idx) => {
        const ar = getGalleryCellAspectRatio(effectiveLayout, idx, n);
        return (
          <Box key={idx} sx={{ ...getGalleryItemGridStyle(effectiveLayout, idx, n), minWidth: 0, alignSelf: 'stretch' }}>
            {renderCell(img, ar)}
          </Box>
        );
      })}
    </Box>
  );
}

export default GalleryImageMosaic;
